/* Eden — consent bar.
   Shopify's own banner is turned off in the admin; this takes its place and
   writes the answer back through the Customer Privacy API, so the storefront
   gates tracking exactly as it would have.

   The API also decides whether a bar is needed at all: shouldShowBanner() is
   false outside the regions that require consent, and false once this visitor
   has already answered. Nothing is shown in those cases.

   Any element with [data-eden-consent-open] — or a link to #cookies — reopens
   the bar, which is what lets a visitor change their mind later. */
(() => {
  const bar = document.getElementById('eden-consent');
  if (!bar) return;

  const prefs = bar.querySelector('#eden-consent-prefs');
  const boxes = Array.from(prefs.querySelectorAll('input[data-consent]'));
  const act = (name) => bar.querySelector(`[data-consent-action="${name}"]`);
  const CATEGORIES = ['analytics', 'marketing', 'preferences'];

  let api = null;
  let opener = null;   // element to send focus back to when reopened from a link

  const load = () => new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy?.setTrackingConsent) return resolve(window.Shopify.customerPrivacy);
    if (!window.Shopify?.loadFeatures) return resolve(null);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
      resolve(err ? null : window.Shopify.customerPrivacy);
    });
  });

  const open = () => {
    bar.hidden = false;
    // One frame so the transition has a start value to move from.
    requestAnimationFrame(() => bar.classList.add('is-open'));
  };

  const close = () => {
    bar.classList.remove('is-open');
    const done = () => { bar.hidden = true; opener?.focus(); opener = null; };
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) done();
    else bar.addEventListener('transitionend', done, { once: true });
  };

  /* When Shopify cannot record the answer — the shop's consent management is not
     configured — remember it here so the bar asks once instead of on every page.
     This only silences the bar: the answer is NOT registered, and tracking keeps
     following the shop's default until consent management is set up. */
  const LOCAL = 'eden-consent-answered';
  const remembered = () => { try { return !!localStorage.getItem(LOCAL); } catch { return false; } };
  const remember = () => { try { localStorage.setItem(LOCAL, new Date().toISOString()); } catch { /* private mode */ } };

  /** Write the answer, then step aside. Shopify persists it for this visitor. */
  const save = (consent) => {
    /* The API is asymmetric, and it is a trap: currentVisitorConsent() hands back
       the strings 'yes' / 'no' / '', while setTrackingConsent expects booleans.
       Passing the strings back returns a misleading wrapper error,
       "Cannot read properties of undefined (reading 'consentManagement')",
       whose real cause only shows in the GraphQL response body:
       "Argument 'marketing' on InputObject 'VisitorConsent' has an invalid
       value (\"no\"). Expected type 'Boolean'." */
    const payload = {
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      preferences: !!consent.preferences,
      // Not a French requirement, but it has to travel with the rest.
      sale_of_data: !!consent.marketing,
    };
    const fallback = (reason) => {
      console.warn('[eden-consent] Shopify did not record the consent:', reason);
      remember();
      close();
    };
    try {
      api.setTrackingConsent(payload, (res) => {
        if (res && res.error) fallback(res.error);
        // Remember on success too. shouldShowBanner() can keep returning true
        // after a valid answer, which is what made the bar come back on every
        // page; the stored answer below is what actually settles it.
        else { remember(); close(); }
      });
    } catch (err) {
      fallback(err.message);
    }
  };

  const all = (value) => Object.fromEntries(CATEGORIES.map((c) => [c, value]));
  const fromBoxes = () => Object.fromEntries(CATEGORIES.map((c) => {
    const box = boxes.find((b) => b.dataset.consent === c);
    return [c, !!box?.checked];
  }));

  act('accept').addEventListener('click', () => save(all(true)));
  act('decline').addEventListener('click', () => save(all(false)));
  act('save').addEventListener('click', () => save(fromBoxes()));

  act('customise').addEventListener('click', () => {
    const showing = !prefs.hidden;
    prefs.hidden = showing;
    act('customise').setAttribute('aria-expanded', String(!showing));
    // With the detail open, "Accepter" would contradict the boxes; offer Save.
    act('accept').hidden = !showing;
    act('save').hidden = showing;
    if (!showing) boxes[0]?.focus();
  });

  load().then((cp) => {
    if (!cp) return;             // API unavailable: stay out of the way entirely
    api = cp;

    /** Has this visitor already answered? Any category holding 'yes' or 'no'
        means yes — an unanswered visitor has empty strings throughout. This is
        the reliable signal; shouldShowBanner() alone is not. */
    const answered = () => {
      try {
        const c = cp.currentVisitorConsent() || {};
        return CATEGORIES.some((k) => c[k] === 'yes' || c[k] === 'no');
      } catch { return false; }
    };

    let needed = true;
    try { needed = cp.shouldShowBanner(); } catch { needed = true; }

    // Reopening stays available even when no bar is due, so a visitor who has
    // already answered can still change their mind.
    const bind = () => {
      document.querySelectorAll('[data-eden-consent-open], a[href="#cookies"]').forEach((el) => {
        if (el.dataset.edenConsentBound) return;
        el.dataset.edenConsentBound = '1';
        el.addEventListener('click', (e) => {
          e.preventDefault();
          opener = el;
          try {
            const current = cp.currentVisitorConsent() || {};
            boxes.forEach((b) => { b.checked = current[b.dataset.consent] === 'yes'; });
          } catch { /* defaults stay unchecked */ }
          try { localStorage.removeItem(LOCAL); } catch { /* private mode */ }
          prefs.hidden = false;
          act('customise').setAttribute('aria-expanded', 'true');
          act('accept').hidden = true;
          act('save').hidden = false;
          open();
        });
      });
    };
    bind();
    document.addEventListener('shopify:section:load', bind);

    if (needed && !answered() && !remembered()) setTimeout(open, 700);
  });
})();
